import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';

const PWA_TRANSLATIONS = {
  en: { title: 'Add to Home Screen', install: 'Install App', ios: 'Tap the Share button, then "Add to Home Screen" to use Infinity Business as an app.', android: 'Install Infinity Business for quick access from your home screen.' },
  de: { title: 'Zum Startbildschirm', install: 'App installieren', ios: 'Tippen Sie auf "Teilen" und dann "Zum Home-Bildschirm" um Infinity Business als App zu nutzen.', android: 'Installieren Sie Infinity Business für schnellen Zugriff.' },
  hr: { title: 'Dodaj na početni zaslon', install: 'Instaliraj', ios: 'Dodirnite gumb za dijeljenje, zatim "Dodaj na početni zaslon" za korištenje aplikacije.', android: 'Instalirajte Infinity Business za brzi pristup.' },
  es: { title: 'Agregar a la pantalla', install: 'Instalar', ios: 'Toca el botón Compartir y luego "Agregar a pantalla de inicio".', android: 'Instala Infinity Business para acceso rápido.' },
  fr: { title: "Ajouter à l'écran", install: 'Installer', ios: "Appuyez sur Partager puis \"Ajouter à l'écran d'accueil\".", android: "Installez Infinity Business pour un accès rapide." },
  it: { title: 'Aggiungi alla Home', install: 'Installa', ios: 'Tocca il pulsante Condividi e poi "Aggiungi alla schermata Home".', android: 'Installa Infinity Business per un accesso rapido.' },
  pt: { title: 'Adicionar à tela', install: 'Instalar', ios: 'Toque no botão Compartilhar e depois "Adicionar à Tela de Início".', android: 'Instale o Infinity Business para acesso rápido.' },
  ru: { title: 'На главный экран', install: 'Установить', ios: 'Нажмите "Поделиться", затем "На экран Домой".', android: 'Установите Infinity Business для быстрого доступа.' },
  zh: { title: '添加到主屏幕', install: '安装', ios: '点击分享按钮，然后选择"添加到主屏幕"。', android: '安装Infinity Business以便快速访问。' },
  nl: { title: 'Toevoegen aan beginscherm', install: 'Installeren', ios: 'Tik op Delen en vervolgens "Zet op beginscherm".', android: 'Installeer Infinity Business voor snelle toegang.' },
  sl: { title: 'Dodaj na začetni zaslon', install: 'Namesti', ios: 'Tapnite na gumb za deljenje, nato "Dodaj na začetni zaslon".', android: 'Namestite Infinity Business za hiter dostop.' },
};

export default function PWAInstallPrompt() {
  const { lang } = useI18n();
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const pt = PWA_TRANSLATIONS[lang] || PWA_TRANSLATIONS.en;

  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    const dismissed = sessionStorage.getItem('pwa_dismissed');
    if (!isMobile || isStandalone || dismissed) return;

    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    if (iOS) {
      const timer = setTimeout(() => setShow(true), 3000);
      return () => clearTimeout(timer);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShow(true), 2000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
    setShow(false);
    sessionStorage.setItem('pwa_dismissed', '1');
  };

  const dismiss = () => {
    setShow(false);
    sessionStorage.setItem('pwa_dismissed', '1');
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[60] max-w-md mx-auto animate-in slide-in-from-bottom duration-300" data-testid="pwa-install-prompt">
      <div className="bg-bg-card border border-white/10 rounded-2xl p-4 shadow-2xl backdrop-blur-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-primary/20 flex items-center justify-center flex-shrink-0">
            <Smartphone size={20} className="text-brand-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-sm text-txt-primary mb-0.5">{pt.title}</h3>
            <p className="text-xs text-txt-muted leading-relaxed">
              {isIOS ? pt.ios : pt.android}
            </p>
          </div>
          <button onClick={dismiss} className="text-txt-muted hover:text-white p-1" data-testid="pwa-dismiss">
            <X size={16} />
          </button>
        </div>
        {!isIOS && (
          <button onClick={handleInstall} data-testid="pwa-install-btn"
            className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-primary text-white rounded-xl text-sm font-medium hover:opacity-90 transition-all">
            <Download size={16} /> {pt.install}
          </button>
        )}
      </div>
    </div>
  );
}
